import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageUploadProps {
  images: string[];
  onChange: (images: string[]) => void;
  maxImages?: number;
  maxSizeMB?: number;
  className?: string;
}

const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export function ImageUpload({
  images,
  onChange,
  maxImages = 10,
  maxSizeMB = MAX_FILE_SIZE_MB,
  className,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert file to base64 data URL
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  // Validate file
  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return `Óleyft skráarsnið. Leyfð snið: JPG, PNG, WebP`;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      return `Skrá er of stór (${sizeMB.toFixed(1)} MB). Hámark: ${maxSizeMB} MB`;
    }

    if (images.length >= maxImages) {
      return `Hámarksfjöldi mynda náð (${maxImages})`;
    }

    return null;
  };

  // Handle file selection
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setError(null);

      const fileArray = Array.from(files);
      const remainingSlots = maxImages - images.length;
      const filesToProcess = fileArray.slice(0, remainingSlots);

      try {
        const newImages: string[] = [];

        for (const file of filesToProcess) {
          const validationError = validateFile(file);
          if (validationError) {
            setError(validationError);
            return;
          }

          const base64 = await fileToBase64(file);
          newImages.push(base64);
        }

        onChange([...images, ...newImages]);

        if (fileArray.length > remainingSlots) {
          setError(`Aðeins ${remainingSlots} myndir voru hlaðið upp (hámark: ${maxImages})`);
        }
      } catch (err) {
        setError('Villa kom upp við að lesa mynd');
        console.error('File upload error:', err);
      }
    },
    [images, maxImages, maxSizeMB, onChange]
  );

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // Remove image
  const handleRemove = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
    setError(null);
  };

  // Trigger file input click
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const canUploadMore = images.length < maxImages;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Upload Area */}
      {canUploadMore && (
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'relative border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer',
            'min-h-[120px] flex flex-col items-center justify-center gap-3',
            isDragging
              ? 'border-prima-500 bg-prima-50'
              : 'border-gray-300 hover:border-prima-400 hover:bg-gray-50'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            onChange={handleInputChange}
            className="hidden"
          />

          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-gray-400" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                Smelltu til að velja myndir eða dragðu hingað
              </p>
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG eða WebP (hámark {maxSizeMB} MB)
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {images.length} af {maxImages} myndum
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
            >
              <img
                src={image}
                alt={`Mynd ${index + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Primary Badge */}
              {index === 0 && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-prima-600 text-white shadow-sm">
                    <ImageIcon className="w-3 h-3" />
                    Aðalmynd
                  </span>
                </div>
              )}

              {/* Delete Button */}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className={cn(
                  'absolute top-2 right-2 p-1.5 rounded-full',
                  'bg-red-600 text-white shadow-md',
                  'opacity-0 group-hover:opacity-100 transition-opacity',
                  'hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500',
                  'min-h-[44px] min-w-[44px] flex items-center justify-center'
                )}
                aria-label={`Eyða mynd ${index + 1}`}
              >
                <X className="w-4 h-4" />
              </button>

              {/* Image Number */}
              <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/50 backdrop-blur-sm rounded text-xs text-white">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && !canUploadMore && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <ImageIcon className="w-12 h-12 mb-3" />
          <p className="text-sm">Engar myndir</p>
        </div>
      )}

      {/* Info */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-800">
          <p className="font-medium mb-1">Ábending:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Fyrsta myndin verður aðalmyndin sem birtist í listanum</li>
            <li>Þú getur hlaðið upp allt að {maxImages} myndum</li>
            <li>Myndir eru vistaðar í vafranum (localStorage)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
